import React, { useState, useMemo } from 'react';
import { Justify } from 'tea-component';

import CardSlot from '@/pages/dashboard/components/CardSlot';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import {
  exportExcel,
  formatExeclCommon,
  formatExeclNum,
  formatExeclSharePercent,
  formatNum,
  formatPercentNum,
  getChartDateFormatString,
  getIsErrorValue,
  upperFirstConcat,
} from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { useLocal, useSelectorMap, useWeekDay } from '@/pages/dashboard/utils/customHook';
import {
  formatDataByChartType,
  xAxisHighLight,
  xAxisLabelFormat,
} from '@/pages/dashboard/components/Chart/_config/plug';
import moment from 'moment';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import MiniTable from '@/pages/dashboard/components/MiniTable';

import './index.scss';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import { getBreakDownHeader } from '../../../engagement/_config/utils';
import { momentFormat } from '@/pages/dashboard/utils/_config/filtersTimeFormat';
import { headerText } from '@/pages/dashboard/components/CountryRegionRowItem/constant';
import { TAB_MAP } from '@/pages/dashboard/_config/constant';
import { useIntl } from 'umi';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';

interface Props {
  title: string;
  filterValue?: any;
  tableData?: any;
  tableColumn?: any;
  chartData?: any;
  timeType?: string;
  isUserMetrics?: boolean;
  regionType: any;
  loading: boolean;
  error: boolean;
  hasShare?: boolean;
  isAllUpper?: boolean;
  unit?: string;
  titleAndType:string,
  isUserQuota:string,
  isZero:boolean
}

const RankTop = (props: Props) => {
  const {
    title,
    regionType,
    timeType,
    tableColumn,
    tableData,
    chartData,
    loading,
    error,
    hasShare,
    filterValue,
    isUserMetrics,
    isAllUpper,
    titleAndType,
    isUserQuota,
    isZero,
    unit = '',
  } = props;
  const [chartType, setChartType] = useState('column-chart');
  const { getWeekDay } = useWeekDay()
  const { locale, getLocalText } = useLocal();
  const { formatMessage } = useIntl();
  const revenueList = ['CPA($)']
  const {getUaSelectorText} = useSelectorMap();
  
  function formatterFunc(param: any) {
    let data: Array<{}> = [];
    const metricsNameText: any = {
      Return: 'return_return',
      Retrun: 'return_return',
      Register: 'ua_Register_chart',
      'CPA($)': 'ua_table_cpa',
      LTV7: 'ua_LTV7',
      New: 'engagement_register_New',
    };

    const metrics = chartData?.[0]?.metrics || '';
    const barName =
      (isAllUpper ? metrics.toUpperCase() : upperFirstConcat(metrics)) + unit;
    const metricsName = metricsNameText[barName]
      ? getLocalText(metricsNameText[barName], revenueList?.includes(barName) ? { unit: getMonetUnit() } : {})
      : barName;

    for (let i = 0; i < param.length; i++) {
      let { color, seriesName } = param[i];
      const date = param[i]?.name
      const list = chartData?.find((item: any) => item?.date_ymd === date)?.list
      const ratioNum = list?.find(
        (item: any) => {
          const name = getUaSelectorText(regionType,item?.full_crz_name)
          return name === seriesName
        }
      )?.share;
      let value = formatNum(param[i].value, false, isUserMetrics);
      data.push({
        name: seriesName,
        metricsNum: value,
        ratioNum: getIsErrorValue(ratioNum)
          ? '-'
          : formatPercentNum(parseFloat(ratioNum), 1),
        color,
      });
    }

    return getEchartTooltipText({
      title: param[0].name,
      metricsName,
      ratioName: getLocalText('common_share'),
      hasRatio: hasShare,
      data,
      isSort: true,
    });
  }

  const handleAxisFormat = (value: any) => {
    return timeType === 'Month'
      ? moment(value).format('YYYY-MM')
      : moment(value).format('MM-DD');
  };

  const axisLabelFormat = (opt: any, timeType: any) => {
    return opt.xAxis?.map((item: any) => {
      return {
        ...item,
        axisLabel: {
          ...item.axisLabel,
          formatter: (value: any) => getChartDateFormatString(timeType, value),
        },
      };
    });
  };

  const setOtherConfigFunc = (opt: any, timeType: any) => {
    opt.xAxis = axisLabelFormat(opt, timeType);
    return opt;
  };

  const chartOptions = useMemo(() => {
    // 图表配置
    return chartData?.[0]?.list?.map((legend: any) => {
      return {
        name: getUaSelectorText(regionType,legend?.full_crz_name),
        stack: 'bar',
        barCategoryGap: '50%',
        data: chartData?.map((item: any) => {
          return {
            x: timeType === 'Month' ? item?.date_md : item?.date_ymd,
            y: item?.list?.find(
              (listItem: any) =>
                (listItem?.crz_name &&
                  legend?.crz_name === listItem?.crz_name) ||
                (listItem?.full_crz_name &&
                  legend?.full_crz_name === listItem?.full_crz_name),
            )?.value,
          };
        }),
      };
    });
  }, [chartData, regionType, locale]);

  const barOptions = formatDataByChartType('bar', chartOptions);
  const lineOptions = formatDataByChartType('line', chartOptions);
  const pieChartConfig = formatDataByChartType('pie', chartOptions);

  const chartMap: any = {
    'column-chart': {
      content: getLocalText('common_bar_chart'),
      component: (
        <BarChart
          height={360}
          legendData={barOptions?.legend ?? []}
          xAxisData={barOptions?.xAxisData ?? []}
          series={barOptions?.series ?? []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string }) => {
                return handleAxisFormat(params.value);
              },
            });
            return setOtherConfigFunc(opt, timeType);
          }}
        />
      ),
    },
    'discount-chart': {
      content: getLocalText('common_line_chart'),
      component: (
        <LineChart
          height={360}
          legendData={lineOptions?.legend ?? []}
          xAxisData={lineOptions?.xAxisData ?? []}
          series={lineOptions?.series ?? []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });
            opt = setOtherConfigFunc(opt, timeType);
            opt = xAxisHighLight(opt, {
              formatter: (params: { value: string }) => {
                return handleAxisFormat(params.value);
              },
            });
            return opt;
          }}
        />
      ),
    },
    'pie-chart-icon': {
      content: getLocalText('common_pie_chart'),
      component: (
        <PieChart
          height={360}
          isNodata={pieChartConfig?.chartData?.length === 0}
          chartData={pieChartConfig?.chartData || []}
          legendData={pieChartConfig?.legendData || []}
          formatterFunc={(params: any) => {
            const { name, value, color } = params;
            const { rangeTime } = filterValue ?? {};
            const time = `${rangeTime[0]}${rangeTime[0] !== rangeTime[1] ? ' ~ ' + rangeTime[1] : ''}`;

            const target = tableData?.find((item: any) => {
              return (
                getUaSelectorText(regionType,item?.full_name) === name
              );
            });

            return getEchartTooltipText({
              title: time,
              metricsName: '',
              ratioName: '',
              hasRatio: true,
              data: [
                {
                  name,
                  metricsNum: value ? formatNum(value) : '-',
                  ratioNum: formatPercentNum(target?.share_of_all, 1),
                  color,
                },
              ],
            });
          }}
        />
      ),
    },
  };

  const downData = useMemo(() => {
    const nameValueObj = getBreakDownHeader(regionType, true, titleAndType);
    const dataArr: any = [
      titleAndType === 'Register'
        ? {
          A: 'Date',
          B: formatMessage({ id: 'common_day_of_week' }),
          C: upperFirstConcat(regionType),
          D: nameValueObj[regionType]?.valueName + unit || '',
          E: formatMessage({ id: 'common_share' }),
        }
        : {
          A: 'Date',
          B: formatMessage({ id: 'common_day_of_week' }),
          C: upperFirstConcat(regionType),
          D: nameValueObj[regionType]?.valueName + unit || '',
        },
    ];

    chartData?.forEach((item) => {
      item?.list?.forEach((e) => {
        dataArr.push(
          titleAndType === 'Register'
            ? {
              A:
                timeType === 'Month'
                  ? momentFormat(item.date_ymd, 'YYYY-MM')
                  : item.date_ymd,
              B: item.date_ymd ? getWeekDay(item.date_ymd) : '-',
              C: e.full_crz_name,
              D: isUserQuota
                ? formatExeclNum(isZero ? +e.value : e.value)
                : formatExeclCommon(isZero ? +e.value : e.value),
              E: formatExeclSharePercent(e.share),
            }
            : {
              A:
                timeType === 'Month'
                  ? momentFormat(item.date_ymd, 'YYYY-MM')
                  : item.date_ymd,
              B: item.date_ymd ? getWeekDay(item.date_ymd) : '-',
              C: e.full_crz_name,
              D: isUserQuota
                ? formatExeclNum(isZero ? +e.value : e.value)
                : formatExeclCommon(isZero ? +e.value : e.value),
            },
        );
      });
    });
    return dataArr;
  }, [chartData, locale]);
  const metricsNameText = {
    Return: 'return_return',
    Retrun: 'return_return',
    Register: 'ua_Register_chart',
    'CPA($)': 'ua_table_cpa',
    LTV7: 'ua_LTV7',
    New: 'engagement_register_New',
    'Country': 'engagement_active_activeUsers_breakdown_country',
    'Region': 'engagement_active_activeUsers_breakdown_region',
    'Zone': 'engagement_active_activeUsers_breakdown_zone',
    'Network': 'common_netWork_Type'

  };
  const newRegionType = regionType === 'network_type' ? 'network' : regionType;

  const metrics = chartData?.[0]?.metrics || '';
  // 导出excel
  const downloadDataFunc = () => {
    const ckey = metricsNameText[downData[0].C]
      ? formatMessage({ id: metricsNameText[downData[0].C] }, revenueList?.includes(downData[0].C) ? { unit: getMonetUnit() } : {})
      : downData[0].C;

    const newDownData = downData.map((item, index) => {
      return index === 0 ? {
        ...item,
        A: formatMessage({ id: 'ua_Date' }),
        B: headerText[downData[0].B]
          ? formatMessage({ id: headerText[downData[0].B] }, revenueList?.includes(downData[0].B) ? { unit: getMonetUnit() } : {})
          : downData[0].B,
        C: ckey,
        D: headerText[downData[0].D]
          ? formatMessage({ id: headerText[downData[0].D] }, revenueList?.includes(downData[0].D) ? { unit: getMonetUnit() } : {})
          : downData[0].D,
      }
        : {
          ...item,
          B: getUaSelectorText(newRegionType, item.B),
        }
    }

    );
    const enTitle = `${isAllUpper ? metrics?.toUpperCase() : upperFirstConcat(metrics)
      }_of_${regionType === 'network_type' ? '' : 'Top_10_'}${TAB_MAP[regionType]
      }`;
    if (timeType === 'Month') {
      //  月粒度删除周的列
      newDownData.map(item => { delete item.B })
    }
    exportExcel(locale === 'zh-CN' ? title : enTitle, [...newDownData]);
  };

  const isTableNoData = !tableData || tableData?.length === 0;
  const isChartNoData = !chartData || chartData?.length === 0;
  const iconList = [
    { icon: 'column-chart', context: getLocalText('common_bar_chart') },
    { icon: 'discount-chart', context: getLocalText('common_line_chart') },
  ];
  if (isUserMetrics) {
    iconList.push({
      icon: 'pie-chart-icon',
      context: getLocalText('common_pie_chart'),
    });
  }

  return (
    <CardSlot
      isEyes={true}
      title={<div className="page-region-title">{title}</div>}
      iconList={iconList}
      iconShow={isChartNoData}
      activeTab={chartType}
      getChange={(value: any) => {
        setChartType(value);
      }}
      onDownload={downloadDataFunc}
    >
      <Justify
        left={
          <Abnormal
            style={{ height: '390px', fontSize: '14px' }}
            component={
              <MiniTable
                loading={false}
                height={390}
                className="FooterFixed"
                tableOption={tableColumn}
                tableList={tableData}
                recordKey={'full_name'}
              />
            }
            isLoading={loading}
            isError={error}
            isNoData={isTableNoData}
          />
        }
        right={
          <div className="dbd-ua-rank-chart">
            <Abnormal
              style={{ height: '100%', fontSize: '14px' }}
              component={chartMap?.[chartType]?.component}
              isLoading={loading}
              isError={error}
              isNoData={isChartNoData}
            />
          </div>
        }
      />
    </CardSlot>
  );
};

export default RankTop;
